import { useEffect, useState } from "react";
import { Box, Button, Stack, Grid, IconButton, InputAdornment } from "@vapor/react-material";
import Modal from "@vapor/react-material/Modal";
import { modalStyleUltraLarge } from "../businessLogic/styles";
import { useTranslation } from "@1f/react-sdk";
import TextField from "@vapor/react-material/TextField";
import ExtendedTypography from "@vapor/react-extended/ExtendedTypography";
import { LocalizationProvider } from "@vapor/react-x-date-pickers/LocalizationProvider";
import { DatePicker } from "@vapor/react-x-date-pickers/DatePicker";
import { AdapterDateFns } from "@vapor/react-x-date-pickers/AdapterDateFns";
import { it } from "date-fns/locale";
import { useSnackbar } from "notistack";
import { isDateValid } from "../businessLogic/helpers/dateHelpers";
import VaporTag from "@vapor/react-custom/VaporTag";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

const TAG_COLORS = ["#1976d2", "#388e3c", "#f57c00", "#d32f2f", "#7b1fa2", "#0288d1", "#00796b", "#c2185b"];
const getRandomTagColor = () => TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)];

const getFormattedDateFromISO = (isoString) => {
    if (!isoString) return "";
    try {
        return isoString.split("T")[0];
    } catch {
        return "";
    }
};

const getYearFromISO = (isoString) => {
    if (!isoString) return "";
    try {
        return isoString.split("-")[0];
    } catch {
        return "";
    }
};

const buildDefaultConservationData = (allegato) => ({
    cct: {
        PeriodoRiferimentoAnno: getYearFromISO(allegato?.createdOn),
        DataDocumento: getFormattedDateFromISO(allegato?.createdOn),        
        DataDocumentoAntiriciclaggio: getFormattedDateFromISO(allegato?.createdOn),
        TipoDocumento: allegato?.displayedName || allegato?.name || "Documento",
        Oggetto: allegato?.displayedName || allegato?.name || "Documento",
        ParoleChiave: [],
    },
    document: {
        nomeUTE: "",
    },
});

export const ModalConservazione = ({ show, onOk, onClose, currentAllegato }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [conservationData, setConservationData] = useState(buildDefaultConservationData(null));
    const [newKeyword, setNewKeyword] = useState("");

    useEffect(() => {
        if (!show) {
            setIsSubmitting(false);
            setNewKeyword("");
            return;
        }
        setIsSubmitting(false);
        setNewKeyword("");
        setConservationData(buildDefaultConservationData(currentAllegato));
    }, [show, currentAllegato]);

    const handleConservationDataChange = (field, value) => {
        setConservationData((prev) => ({
            ...prev,
            cct: {
                ...prev.cct,
                [field]: value,
            },
        }));
    };

    const handleAddKeyword = () => {
        const kw = newKeyword.trim();
        if (!kw) return;
        if (conservationData.cct.ParoleChiave.some((k) => k.label === kw)) return;
        handleConservationDataChange("ParoleChiave", [...conservationData.cct.ParoleChiave, { label: kw, color: getRandomTagColor() }]);
        setNewKeyword("");
    };

    const handleRemoveKeyword = (kw) => {
        handleConservationDataChange(
            "ParoleChiave",
            conservationData.cct.ParoleChiave.filter((k) => k.label !== kw)
        );
    };

    const validateConservationData = () => {
        const requiredFields = [
            { key: "DataDocumento", label: "Data Documento" },
            { key: "TipoDocumento", label: "Nome Documento" },
            { key: "Oggetto", label: "Oggetto" },
        ];

        const errors = requiredFields
            .filter((field) => !conservationData.cct[field.key] || conservationData.cct[field.key].toString().trim() === "")
            .map((field) => `${field.label} è obbligatorio`);

        return { isValid: errors.length === 0, errors };
    };

    const handleOk = async (e) => {
        if (isSubmitting) return;

        const validation = validateConservationData();
        if (!validation.isValid) {
            validation.errors.forEach((error) => {
                enqueueSnackbar(error, { variant: "error" });
            });
            return;
        }

        setIsSubmitting(true);

        try {
            await onOk({
                event: e,
                metadata: conservationData,
            });
        } catch (error) {
            setIsSubmitting(false);
            throw error;
        }
    };

    const handleCancel = (e) => {
        onClose(e);
    };

    return (
        <Modal
            key="modal-conservazione"
            aria-labelledby="modal-conservazione-title"
            aria-describedby="modal-conservazione-description"
            open={show}
            onOk={handleOk}
            onCancel={handleCancel}
            onClose={handleCancel}
        >
            <Box sx={{ ...modalStyleUltraLarge, display: "flex", flexDirection: "column" }}>
                {/* Header */}
                <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                    <ExtendedTypography variant="titleSmall" color="primary.main">
                        Invio in conservazione
                    </ExtendedTypography>
                    {currentAllegato?.displayedName && (
                        <ExtendedTypography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
                            {currentAllegato.displayedName}
                        </ExtendedTypography>
                    )}
                </Box>

                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
                    <Grid container spacing={2} sx={{ flex: 1, overflowY: "auto", px: 3, py: 2, alignContent: "flex-start" }}>

                        <Grid item xs={12}>
                            <ExtendedTypography variant="subtitle2" sx={{ color: "primary.main" }}>
                                Dati per la conservazione
                            </ExtendedTypography>
                        </Grid>

                        {/* Row 1: Data Documento + Anno Riferimento (auto, read-only) */}
                        <Grid item xs={6}>
                            <DatePicker
                                label="Data Documento"
                                fullWidth
                                closeOnSelect
                                format="dd/MM/yyyy"
                                mask="__/__/____"
                                maxDate={new Date()}
                                value={isDateValid(conservationData.cct.DataDocumento) ? new Date(conservationData.cct.DataDocumento) : null}
                                onChange={(newValue) => {
                                    if (newValue && !isNaN(newValue)) {
                                        const isoDate = newValue.toISOString().split("T")[0];
                                        handleConservationDataChange("DataDocumento", isoDate);
                                        handleConservationDataChange("PeriodoRiferimentoAnno", isoDate.split("-")[0]);
                                        handleConservationDataChange("DataDocumentoAntiriciclaggio", isoDate);
                                    } else {
                                        handleConservationDataChange("DataDocumento", "");
                                        handleConservationDataChange("PeriodoRiferimentoAnno", "");
                                        handleConservationDataChange("DataDocumentoAntiriciclaggio", "");
                                    }
                                }}
                                slotProps={{
                                    textField: {
                                        required: true,
                                        size: "small",
                                        fullWidth: true,
                                        placeholder: "dd/MM/yyyy",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                label="Anno Riferimento"
                                fullWidth
                                size="small"
                                value={conservationData.cct.PeriodoRiferimentoAnno}
                                inputProps={{ readOnly: true }}
                            />
                        </Grid>                        

                        {/* Row 2: Nome Documento + Oggetto */}
                        <Grid item xs={6}>
                            <TextField
                                label="Nome Documento"
                                fullWidth
                                size="small"
                                required
                                value={conservationData.cct.TipoDocumento}
                                onChange={(e) => handleConservationDataChange("TipoDocumento", e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                label="Oggetto"
                                fullWidth
                                size="small"
                                required
                                value={conservationData.cct.Oggetto}
                                onChange={(e) => handleConservationDataChange("Oggetto", e.target.value)}
                            />
                        </Grid>

                        {/* Row 3: Parole Chiave con tag */}
                        <Grid item xs={12}>
                            <TextField
                                label="Parole chiave"
                                fullWidth
                                size="small"
                                value={newKeyword}
                                onChange={(e) => setNewKeyword(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleAddKeyword();
                                    }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={handleAddKeyword} disabled={!newKeyword.trim()}>
                                                <AddIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sx={{ minHeight: "40px" }}>
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                {conservationData.cct.ParoleChiave.map((kw) => (
                                    <VaporTag
                                        key={kw.label}
                                        variant="standard"
                                        label={kw.label}
                                        deleteIcon={<CloseIcon />}
                                        onDelete={() => handleRemoveKeyword(kw.label)}
                                        sx={{ bgcolor: kw.color, color: "#fff", "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff" } } }}
                                    />
                                ))}
                            </Stack>
                        </Grid>

                    </Grid>
                </LocalizationProvider>

                <Stack
                    direction="row"
                    spacing={1}
                    sx={{ px: 3, py: 2, mt: "auto", bgcolor: "#F2F5F8", borderTop: "1px solid", borderColor: "divider" }}
                    justifyContent="flex-end"
                    alignItems="center"
                >
                    <Button variant="outlined" size="small" onClick={handleCancel}>
                        {t("actions.annulla")}
                    </Button>
                    <Button variant="contained" size="small" onClick={handleOk} disabled={isSubmitting}>
                        Invia in conservazione
                    </Button>
                </Stack>
            </Box>
        </Modal>
    );
};
